import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { TIER_ORDER, TIERS, STATE_NAME } from "../data/broker";

const STATE_CODES = Object.keys(STATE_NAME).sort();

const emptyForm = {
  name: "",
  state: "",
  company_hubspot_link: "",
  note: "",
  tier: "",
  is_no_contact: false,
  is_abm: true,
};

export default function FirmDialog({ open, initial, onCancel, onSave }) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        name: initial.name || "",
        state: initial.state || "",
        company_hubspot_link: initial.company_hubspot_link || "",
        note: initial.note || "",
        tier: initial.tier || "",
        is_no_contact: Boolean(initial.is_no_contact),
        is_abm: initial.is_abm !== false,
      });
    } else {
      setForm({ ...emptyForm, is_no_contact: true, tier: "t5" });
    }
    setErr("");
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) {
      setErr("Firm name is required.");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onCancel() : null)}>
      <DialogContent
        className="!bg-[#0a0a0c] !border-white/10 !text-zinc-100 !max-w-md"
        data-testid="firm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight text-zinc-50">
            {isEdit ? "Edit Firm" : "New Firm"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-[12px]">
            {isEdit ? "Update this firm." : "Add a firm (defaults to Tier 5 · No Traction)."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Firm Name *">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="ff-name" required />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="State">
              <select value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} data-testid="ff-state">
                <option value="">—</option>
                {STATE_CODES.map((s) => (
                  <option key={s} value={s}>{s} · {STATE_NAME[s]}</option>
                ))}
              </select>
            </Field>
            <Field label="Tier">
              <select value={form.tier} onChange={(e) => set("tier", e.target.value)} className={inputCls} data-testid="ff-tier">
                <option value="">— (derived from contacts)</option>
                {TIER_ORDER.map((tk) => (
                  <option key={tk} value={tk}>{TIERS[tk].short} · {TIERS[tk].label}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="HubSpot Company Link">
            <input value={form.company_hubspot_link} onChange={(e) => set("company_hubspot_link", e.target.value)} className={inputCls} data-testid="ff-hublink" />
          </Field>
          <Field label="Notes">
            <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className={inputCls} data-testid="ff-note" />
          </Field>
          <label className="flex items-center gap-2 font-mono text-[11px] text-zinc-400 uppercase tracking-[0.15em]">
            <input
              type="checkbox"
              checked={form.is_no_contact}
              onChange={(e) => set("is_no_contact", e.target.checked)}
              data-testid="ff-nocontact"
            />
            No-Contact Firm (Target Only)
          </label>
          <label className="flex items-center gap-2 font-mono text-[11px] text-zinc-400 uppercase tracking-[0.15em]">
            <input
              type="checkbox"
              checked={form.is_abm}
              onChange={(e) => set("is_abm", e.target.checked)}
              data-testid="ff-abm"
            />
            ABM · Target Account List
          </label>

          {err && (
            <div className="border border-rose-500/50 bg-rose-500/10 text-rose-200 px-3 py-2 font-mono text-[11px] tracking-[0.16em] uppercase">
              {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-white/10 hover:border-white/30 text-zinc-300 font-mono text-[11px] uppercase tracking-[0.16em]"
              data-testid="firm-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-cyan-400/60 bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-100 font-mono text-[11px] uppercase tracking-[0.16em] disabled:opacity-50"
              data-testid="firm-save"
            >
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Firm"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "w-full bg-[#0d0d10] border border-white/10 focus:border-cyan-400/50 outline-none px-3 py-2 font-mono text-[12px] text-zinc-100 placeholder:text-zinc-600";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
